import React, { useEffect, useState } from 'react'
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridFilterChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { DealDtoFilterKey } from 'api/Clients/Types/DealDto'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { Pager } from 'components/components/Pager'
import { Spinner } from 'components/Spinner'
import { calculatePageIndex, calculateSkipIndex } from 'features/DataGrid/helpers'
import { initialState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOneWithFetchingUpdate } from 'utils/api/useFetchOneWithFetchingUpdate'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { BuildQueryProps, FilterLogic } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { useDealsBuildQuery } from '../../../Brokerage/Deals/DealsTableFilter'
import { dealsColumns } from './columns'

interface TransactionDetailsNotesProps {
  transaction: TransactionDto
}

const TransactionDetailsDealsPage: React.FC<TransactionDetailsNotesProps> = ({ transaction }) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const { getQuery, getSort } = useDealsBuildQuery()

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [filterState, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [],
  })
  const [sortState, setSortState] = useState<SortDescriptor[]>([])
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  const { callback, forceRefresh } = useCallbackWithForceRefresh(() => {
    const dealsQuery = createDealsQuery(transaction, filterState)
    const searchQuery = getQuery(dealsQuery)
    const sortQuery = getSort()
    return partnershipsApiClient.getDeals({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: calculatePageIndex(paginationState.skip, paginationState.take),
      pageSize: paginationState.take,
    })
  }, [])
  const { data, isLoading } = useFetchOneWithFetchingUpdate(callback)
  const deals = data?.items || []
  const total = data?.totalResults || 0

  useEffect(() => {
    if (data) {
      setTableData({ ...tableData, meta: data })
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!isLoading) {
        setPaginationState((page) => ({ ...page, skip: 0 }))
        forceRefresh()
      }
    }, 500)
    return () => clearTimeout(timeoutId)
  }, [filterState.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(event.filter)
  }

  const handleSortChange = (event: GridSortChangeEvent) => {
    setSortState(event.sort)
    forceRefresh()
  }
  const handlePageChange = (pageIndex: number) => {
    setPaginationState({
      ...paginationState,
      skip: calculateSkipIndex(pageIndex, paginationState.take),
    })
    forceRefresh()
  }

  const handlePageSizeChange = (pageSize: number) => {
    setPaginationState({ ...paginationState, take: pageSize })
    forceRefresh()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div className='records-clients'>
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={deals} columns={dealsColumns}>
          <Grid
            resizable
            filterable={!!filterState}
            data={deals}
            sort={sortState}
            filter={filterState}
            total={total}
            take={paginationState.take}
            skip={paginationState.skip}
            onSortChange={handleSortChange}
            onFilterChange={handleFilterChange}
            pageable={pageableSettings}
            pager={() => (
              <Pager
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                page={calculatePageIndex(paginationState.skip, paginationState.take)}
                pageSize={paginationState.take}
                take={paginationState.take}
                skip={paginationState.skip}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {dealsColumns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </div>
  )
}

const createDealsQuery = (
  transaction: TransactionDto,
  filter: CompositeFilterDescriptor
): BuildQueryProps => {
  const initialGridState = initialState()

  return {
    ...initialGridState.filter,
    logic: filter.logic,
    filters: filter.filters,
    defaultFilters: {
      logic: FilterLogic.AND,
      filters: [
        {
          field: DealDtoFilterKey.walletTransaction,
          operator: '==',
          value: transaction.referenceId,
        },
      ],
    },
  }
}

export default TransactionDetailsDealsPage
